import { NavLink } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownUp,
  Award,
  CreditCard,
  FileSignature,
  FileText,
  Gauge,
  Gift,
  Handshake,
  IdCard,
  Logs,
  Megaphone,
  PlayCircle,
  Plus,
  RefreshCw,
  Repeat,
  RotateCcw,
  Server,
  Settings,
  ShieldCheck,
  Tag,
  TestTube,
  Users,
  Wallet,
} from 'lucide-react';
import { Logo, cn } from '@pnl/ui';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  permission?: string;
}

const sections: { label: string; items: NavItem[] }[] = [
  {
    label: 'Operação',
    items: [
      { to: '/', label: 'Dashboard', icon: Gauge },
      { to: '/traders', label: 'Traders', icon: Users, permission: 'traders:read' },
      // Dossiê: tudo de um trader numa tela, inclusive o que ele vê no app.
      {
        to: '/traders/dossie',
        label: 'Dossiê do trader',
        icon: IdCard,
        permission: 'traders:read',
      },
      // Contratos vive em Operação (não em Catálogo): é a lista onde o trader cai
      // assim que compra e onde a assinatura é acompanhada até virar pedido de
      // ativação — passo do fluxo operacional, logo antes de "Ativações".
      {
        to: '/contracts',
        label: 'Contratos',
        icon: FileSignature,
        permission: 'documents:review',
      },
      {
        to: '/accounts/funded',
        label: 'Contas Funded',
        icon: Award,
        permission: 'accounts:read',
      },
      {
        to: '/accounts/testing',
        label: 'Contas em Teste',
        icon: TestTube,
        permission: 'accounts:read',
      },
      {
        to: '/activations',
        label: 'Ativações',
        icon: PlayCircle,
        permission: 'accounts:read',
      },
      // Metas atingidas: fila de validação MANUAL antes de a conta virar funded.
      // Fica logo após Ativações porque é o mesmo tipo de trabalho — analisar e
      // liberar.
      {
        to: '/metas',
        label: 'Metas atingidas',
        icon: Award,
        permission: 'accounts:read',
      },
      {
        to: '/restarts',
        label: 'Restarts pendentes',
        icon: RotateCcw,
        permission: 'accounts:read',
      },
      {
        to: '/forex',
        label: 'Forex',
        icon: RefreshCw,
        permission: 'subscriptions:read',
      },
      { to: '/withdrawals', label: 'Saques', icon: Wallet, permission: 'withdrawals:read' },
      { to: '/afiliados', label: 'Afiliados', icon: Handshake, permission: 'affiliates:read' },
      { to: '/replicators', label: 'Replicadores', icon: Repeat },
      {
        to: '/delinquencies',
        label: 'Inadimplência',
        icon: AlertTriangle,
        permission: 'subscriptions:read',
      },
    ],
  },
  {
    label: 'Catálogo',
    items: [
      { to: '/plans', label: 'Planos', icon: Tag, permission: 'plans:read' },
      { to: '/coupons', label: 'Cupons', icon: Tag, permission: 'coupons:read' },
      { to: '/comunicados', label: 'Comunicados', icon: Megaphone, permission: 'comms:read' },
      { to: '/indicacoes', label: 'Indicações', icon: Gift, permission: 'settings:read' },
      {
        to: '/checkout-addons',
        label: 'Produtos Extras',
        icon: Plus,
        permission: 'plans:read',
      },
      {
        to: '/subscriptions',
        label: 'Assinaturas',
        icon: CreditCard,
        permission: 'subscriptions:read',
      },
      {
        to: '/platforms',
        label: 'Plataformas',
        icon: Server,
        permission: 'subscriptions:read',
      },
    ],
  },
  {
    label: 'Conformidade',
    items: [
      { to: '/csv-imports', label: 'Importações CSV', icon: ArrowDownUp, permission: 'csv:upload' },
      { to: '/documents', label: 'Documentos', icon: FileText, permission: 'documents:review' },
      { to: '/terms', label: 'Termos de Uso', icon: FileText, permission: 'settings:read' },
      { to: '/audit', label: 'Audit log', icon: Logs, permission: 'audit:read' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/users', label: 'Usuários & RBAC', icon: ShieldCheck, permission: 'users:read' },
      { to: '/settings', label: 'Configurações', icon: Settings, permission: 'settings:read' },
    ],
  },
];

export interface SidebarProps {
  hasPermission: (key: string) => boolean;
}

export function Sidebar({ hasPermission }: SidebarProps) {
  return (
    <aside className="border-brand-800/50 bg-brand-900 text-brand-100 sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r lg:flex">
      <div className="border-brand-800/50 flex h-16 items-center border-b px-6">
        <Logo tone="dark" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => {
          const visible = section.items.filter(
            (it) => !it.permission || hasPermission(it.permission),
          );
          if (visible.length === 0) return null;
          return (
            <div key={section.label} className="mb-6">
              <p className="text-2xs text-brand-400/70 px-3 pb-1.5 font-semibold uppercase tracking-widest">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {visible.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-brand-500 shadow-soft-dark text-white'
                            : 'text-brand-100 hover:bg-white/5 hover:text-white',
                        )
                      }
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="border-brand-800/50 text-2xs text-brand-300/70 border-t px-6 py-3">
        v0.2 · ambiente <span className="font-mono">{import.meta.env.MODE}</span>
      </div>
    </aside>
  );
}
